import type {jsPDF} from 'jspdf'
import {LVS_COLORS, type LvsColorKey} from '../lvs-colors'
import type {Painter, Point, PathOp} from './types'

/**
 * Painter adapter that writes into a jsPDF page. State (current fill,
 * stroke) is held by jsPDF itself via setFillColor/setDrawColor;
 * PdfPainter is a thin translator from `LvsColorKey` and y-down mm
 * primitives onto jsPDF's API.
 *
 * Note: jsPDF compat mode auto-flips y in moveTo/lineTo/curveTo so
 * user-space mm with y-down go to page coordinates correctly. The
 * setCurrentTransformationMatrix path is intentionally avoided — see
 * commit 3b65087 for the glyph-positioning bug it caused.
 */
export class PdfPainter implements Painter {
    constructor(private readonly pdf: jsPDF) {}

    private applyFill(key: LvsColorKey): void {
        const cmyk = LVS_COLORS[key].cmyk100
        this.pdf.setFillColor(cmyk[0] / 100, cmyk[1] / 100, cmyk[2] / 100, cmyk[3] / 100)
    }

    private applyStroke(key: LvsColorKey, lineWidth: number): void {
        const cmyk = LVS_COLORS[key].cmyk100
        this.pdf.setDrawColor(cmyk[0] / 100, cmyk[1] / 100, cmyk[2] / 100, cmyk[3] / 100)
        this.pdf.setLineWidth(lineWidth)
    }

    fillRoundedRect(
        fill: LvsColorKey,
        x: number,
        y: number,
        w: number,
        h: number,
        r: number
    ): void {
        const safeR = Math.max(0, r)
        this.applyFill(fill)
        this.pdf.roundedRect(x, y, w, h, safeR, safeR, 'F')
    }

    fillPolygon(fill: LvsColorKey, verts: readonly Point[]): void {
        if (verts.length === 0) return
        this.applyFill(fill)
        this.pdf.moveTo(verts[0].x, verts[0].y)
        for (let i = 1; i < verts.length; i++) {
            this.pdf.lineTo(verts[i].x, verts[i].y)
        }
        this.pdf.fill()
    }

    fillPathOps(fill: LvsColorKey, ops: readonly PathOp[]): void {
        if (ops.length === 0) return
        this.applyFill(fill)
        for (const o of ops) {
            if (o.op === 'm') this.pdf.moveTo(o.x, o.y)
            else if (o.op === 'l') this.pdf.lineTo(o.x, o.y)
            else if (o.op === 'c') {
                this.pdf.curveTo(o.c1x, o.c1y, o.c2x, o.c2y, o.x, o.y)
            }
            // 'h' (close) is handled implicitly by fillEvenOdd below.
        }
        this.pdf.fillEvenOdd()
    }

    strokeLine(
        stroke: LvsColorKey,
        lineWidth: number,
        x1: number,
        y1: number,
        x2: number,
        y2: number
    ): void {
        this.applyStroke(stroke, lineWidth)
        this.pdf.line(x1, y1, x2, y2)
    }

    withClipRoundedRect(
        x: number,
        y: number,
        w: number,
        h: number,
        r: number,
        body: () => void
    ): void {
        const safeR = Math.max(0, r)
        this.pdf.saveGraphicsState()
        try {
            this.pdf.roundedRect(x, y, w, h, safeR, safeR, null)
            this.pdf.clip().discardPath()
            body()
        } finally {
            this.pdf.restoreGraphicsState()
        }
    }
}
